import { useMemo } from 'react';
import { getControlValues } from '../../../shared/services/spc/getControlValues.ts';
import { nullish } from '../../../shared/types/nullish.ts';
import { Position } from '../../../shared/types/protocol.ts';
import { isInTolerance } from '../../../shared/utils/getToleranceRange.ts';
import { MGraphMeasurement } from '../../components/measurement-graph/measurementsGraph.tsx';
import { getUsedTool } from '../../services/getUsedTool.ts';
import { CustomEquipment, ProtocolFinal, RecordDraft, RecordWithProtocol } from '../../types/sharedTypeImpl.ts';
import { evalOrNormalize } from '../../utils/positionInputParse.ts';
import { trpc } from '../../utils/trpc/trpc.ts';


interface Props {
    protocol: ProtocolFinal | nullish;
    prevRecords: RecordWithProtocol[] | nullish;
    currentRecord: RecordDraft | nullish;
    selectedPosNr: string | null;
    maxDataPoints: number;
}

export function useMGraphAdapter({
    protocol,
    prevRecords,
    currentRecord,
    selectedPosNr,
    maxDataPoints,
}: Props) {

    const { data: cmmEquipments } = trpc.equipments.getAllCustom.useQuery(
        { type: 'cmm/vmm' as const },
        { refetchOnWindowFocus: false }
    );

    const measurementsAndCV = useMemo(() => {
        if (!currentRecord || !protocol || !selectedPosNr || !cmmEquipments) return null;

        const position = protocol.positions.find(it => it.positionNumber === selectedPosNr);
        if (!position || position.type !== 'measurement') return null;

        const measurements = getMeasurements(
            currentRecord, protocol, prevRecords, position, cmmEquipments, maxDataPoints
        );

        const values = measurements.map(it => it.value).filter(v => v != null);
        const controlValues = getControlValues(values, position);

        return {
            measurements,
            controlValues,
        };
    }, [currentRecord, protocol, selectedPosNr, cmmEquipments, prevRecords, maxDataPoints]);

    return {
        measurements: measurementsAndCV?.measurements ?? null,
        controlValues: measurementsAndCV?.controlValues ?? null
    };
}

function getMeasurements(
    currentRecord: RecordDraft,
    currentProtocol: ProtocolFinal,
    prevRecords: RecordWithProtocol[] | nullish,
    position: Position,
    cmmEquipments: CustomEquipment[],
    maxDataPoints: number,
): MGraphMeasurement[] {
    const positionNumber = position.positionNumber;

    const allRecords = [
        ...(prevRecords ? [...prevRecords] : []),
        { ...currentRecord, protocol: currentProtocol },
    ];

    const measurements: MGraphMeasurement[] = [];
    for (const record of allRecords) {
        const isCurrentRecord = record === allRecords.at(-1);
        if (record.isDraft && !isCurrentRecord) continue;

        for (const [partIndex, part] of record.parts.entries()) {
            const position = record.protocol.positions.find(it => it.positionNumber === positionNumber);
            const recordPos = part.positionValues.find(it => it.positionNumber === positionNumber);
            if (!position) continue;
            if (position.type === 'visualInspection') continue;
            if (position.nominal != position.nominal) continue;
            if (recordPos == null) continue;

            const valueRaw = (recordPos.value as string | null);
            const valueTemp = isCurrentRecord && valueRaw ? evalOrNormalize(valueRaw) : valueRaw;
            const value = !valueTemp || Number.isNaN(+valueTemp) ? null : valueTemp;

            const isHighlighted = value ? !isInTolerance(value, position) : false;
            measurements.push({
                value,
                isHighlighted,
                date: record.measurementStart!,
                partCount: record.partCount ?? null,
                partIndex,
                hasMultipleParts: record.parts.length > 1,
                source: getUsedTool(recordPos, position, cmmEquipments),
                issues: isHighlighted ? ['Out of tolerance'] : [],
            });
        }
    }

    const truncated = measurements.slice(-maxDataPoints);
    return truncated;
}
